'use client';

import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Toast from '@atoms/Toast';
import Button from '@atoms/new/button';
import { getAuth } from '@services/identity.service';
import { logsToAwsCloudWatch } from 'services/logger.service';

function ErrorFallback({ resetErrorBoundary }) {
  return (
    <div className="fixed bottom-8 left-8 z-9999 animate-slideLeft">
      <Toast
        variant="alert"
        width="w-[400px]"
        showIcon
        header="Something went wrong on our end."
        headerClassName="font-semibold"
        onClose={resetErrorBoundary}
        body={
          <div className="flex gap-4 flex-col">
            <p>
              We couldn't load this interaction. Please try again.
            </p>
            <Button
              onClick={resetErrorBoundary}
              kind="tertiary"
              aria-label="Retry or refresh"
              className="max-w-fit dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-grey-900"
            >
              Try again
            </Button>
          </div>
        }
        position="bottom-left"
      />
    </div>
  );
}

export default function InteractionErrorBoundary({ children, handlePlayIcon }) {
  const [retryCount, setRetryCount] = useState(0);

  const onReset = () => handlePlayIcon();
  const handleError = (error, errorInfo) => {
    setRetryCount((prev) => prev + 1);
    const auth = getAuth();
    logsToAwsCloudWatch({
      logMessage: 'BrowserError(interaction error boundary): Error occurred in the component',
      logData: {
        message: error.message,
        userId: auth?.id || '',
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
      },
      level: 'ERROR',
    });
  };

  return (
    <ErrorBoundary
      fallbackRender={({ resetErrorBoundary }) => (
        <div className="flex flex-col h-full">
          {retryCount === 1 && (children)}
          {retryCount >= 1 && <ErrorFallback
            resetErrorBoundary={resetErrorBoundary}
          />}
        </div>
      )}
      onError={handleError}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
}
